import React, { useEffect, useRef, useState } from "react";
import apiUrl from "../../Apiurl";
import stilePulsanti from "../../interfaccia/stilePulsanti";
import BlockSkeleton from "../../interfaccia/loading/BlockSkeleton";
import TextSkeleton from "../../interfaccia/loading/TextSkeleton";
import useArticleImportRequest, { useArticleImportGet } from "../useArticleImportRequest";
import { generaArrayDateNuovoFinoAl, formatDataPerTasto } from "./parametriImportFornitore";

const baseUrl = apiUrl + "/csv/tracciati/rossignol-v2";
const { buttonClassesWhite, buttonClassesGreen, inputClassesNoMeasure } = stilePulsanti;

// Anteprima e conferma derivano il multipart dallo stesso oggetto, incluso il File.
function formDaSnapshot(snapshot) {
  const form = new FormData();
  form.append("uploaded_file", snapshot.file);
  form.append("mappatura", JSON.stringify(snapshot.mappatura));
  form.append("nuovoFinoAl", snapshot.nuovoFinoAl);
  return form;
}

function Json({ value }) {
  return <pre style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{JSON.stringify(value, null, 2)}</pre>;
}

function Elenco({ titolo, valori = [] }) {
  return (
    <details className="mv2">
      <summary>{titolo}: {valori.length}</summary>
      {valori.length ? <ul>{valori.map((valore, i) => <li key={i}>{valore}</li>)}</ul> : <p>Nessuno</p>}
    </details>
  );
}

function Riepilogo({ dati }) {
  return (
    <div className="pa3 ba bordoColAlt br3">
      <p>Righe: {dati.righe}</p>
      <p>Righe senza EAN: {dati.righeSenzaEan}</p>
      <p>Righe duplicate: {dati.righeDuplicate}</p>
      <Elenco titolo="EAN non trovati" valori={dati.eanNonTrovati} />
      <Elenco titolo="Codici già lavorati" valori={dati.codiciGiaLavorati} />
      <p>Articoli importabili: {dati.articoliImportabili}</p>
      {dati.categorieBotAssegnate != null && <p>Categorie BOT assegnate: {dati.categorieBotAssegnate} su {dati.articoliImportabili} importabili</p>}
    </div>
  );
}

function ArticoloAnteprima({ dati }) {
  const articolo = dati.articolo || {};
  const generici = articolo.datiUlteriori?.generici;
  const collezione = dati.stagioni?.collezione;
  const consigliata = dati.stagioni?.consigliata;
  const categorieBot = dati.categorieBot;
  return (
    <article className="pa3 mv3 ba bordoColAlt br3" aria-label={`Anteprima ${dati.codVS}`}>
      <h4>{dati.codVS} — EAN {dati.ean}</h4>
      <p>Riga del file: {dati.riga}</p>
      {dati.productCode && <p>Product code (solo anteprima): {dati.productCode}</p>}
      <h5>Dati Moda usati</h5>
      <dl>{Object.entries(dati.moda || {}).map(([chiave, valore]) => (
        <React.Fragment key={chiave}><dt className="b">{chiave}</dt><dd>{valore || "—"}</dd></React.Fragment>
      ))}</dl>
      <h5>Messaggi</h5>
      {dati.messaggi?.length ? <ul>{dati.messaggi.map((m, i) => <li key={i}>{m}</li>)}</ul> : <p>Nessun messaggio</p>}
      <h5>Articolo convertito</h5>
      <dl>
        <dt className="b">Brand</dt><dd>{articolo.brand}</dd>
        <dt className="b">Titolo IT</dt><dd>{articolo.descrizioniSuPS?.titolo?.it}</dd>
        <dt className="b">Descrizione breve IT</dt><dd>{articolo.descrizioniSuPS?.descBreve?.it}</dd>
        <dt className="b">Categorie VIG</dt><dd>{articolo.categorieWebAssociate?.join(", ")}</dd>
        <dt className="b">Categoria predefinita VIG</dt><dd>{articolo.categoriaPredefinita}</dd>
        {categorieBot && <>
          <dt className="b">Categorie BOT</dt>
          <dd>{categorieBot.stato === "assegnate"
            ? `${categorieBot.categoryIds.join(", ")} (predefinita: ${categorieBot.defaultCategoryId ?? "nessuna"})`
            : `non assegnate${categorieBot.motivo ? `: ${categorieBot.motivo}` : ""}`}</dd>
        </>}
        <dt className="b">Persona (datiUlteriori)</dt><dd><Json value={generici?.persona} /></dd>
        <dt className="b">Stagione collezione</dt>
        <dd>{collezione ? `${collezione.valueDesc} (fonte: ${collezione.fonte === "stagione_lancio" ? "lancio" : "acquisto"})` : "non determinata"}</dd>
        <dt className="b">Stagione consigliata</dt><dd>{consigliata?.valueDesc || "non determinata"}</dd>
      </dl>
      <details><summary>JSON completo articolo</summary><Json value={dati} /></details>
    </article>
  );
}

function ColonneAttese({ dati, errore }) {
  return (
    <section aria-label="Colonne attese" className="mv3">
      <h3 className="f5 b">Colonne attese nel file</h3>
      {errore && <p role="alert">Impossibile leggere l’elenco delle colonne: {errore}</p>}
      {!dati && !errore && <div role="status" aria-label="Lettura colonne attese"><TextSkeleton lines={4} /></div>}
      {dati && <>
        <p>File {dati.formato?.codifica || "UTF-8"}, separatore {(dati.formato?.separatori || []).join(", ")}, con le
          intestazioni nella prima riga. Le intestazioni possono essere in altra lingua o con spaziature diverse:
          al passo 2 potrai correggere l’abbinamento. Le altre colonne vengono ignorate.</p>
        <table className="collapse w-100">
          <thead><tr>
            <th className="tl pa1">Colonna</th><th className="tl pa1">Obbligatoria</th>
            <th className="tl pa1">Riconosciuta anche come</th><th className="tl pa1">Note</th>
          </tr></thead>
          <tbody>{dati.campi.map(campo => <tr key={campo.chiave} className="bt bordoColAlt">
            <td className="pa1 b">{campo.chiave}</td>
            <td className="pa1">{campo.obbligatorio ? "Sì" : "No"}</td>
            <td className="pa1">{(campo.alias || []).join(", ")}</td>
            <td className="pa1">{campo.nota}</td>
          </tr>)}</tbody>
        </table>
      </>}
    </section>
  );
}

export default function RossignolV2Import({ utente }) {
  const upload = useArticleImportRequest(utente);
  const leggi = useArticleImportGet();
  const [colonneAttese, setColonneAttese] = useState(null);
  const [erroreColonne, setErroreColonne] = useState("");
  const [date] = useState(generaArrayDateNuovoFinoAl);
  const [snapshot, setSnapshot] = useState(() => ({
    file: null,
    mappatura: {},
    nuovoFinoAl: date[0],
  }));
  const snapshotRef = useRef(snapshot);
  // Le generazioni ignorano anche errori/finally obsoleti e risposte dopo unmount.
  // L'importazione non viene mai annullata automaticamente.
  const richieste = useRef({ intestazioni: 0, anteprima: 0, mounted: true, importing: false });
  const [intestazioni, setIntestazioni] = useState(null);
  const [preview, setPreview] = useState(null);
  const [esito, setEsito] = useState(null);
  const [errore, setErrore] = useState("");
  const [letturaFile, setLetturaFile] = useState(false);
  const [letturaAnteprima, setLetturaAnteprima] = useState(false);
  const [importazione, setImportazione] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    leggi(baseUrl + "/campi", { signal: controller.signal })
      .then(setColonneAttese)
      .catch(error => { if (error?.name !== "AbortError") setErroreColonne(error.message); });
    return () => controller.abort();
  }, [leggi]);

  useEffect(() => {
    const corrente = richieste.current;
    corrente.mounted = true;
    return () => {
      corrente.mounted = false;
      corrente.intestazioni++;
      corrente.anteprima++;
    };
  }, []);

  function aggiornaSnapshot(next) {
    if (richieste.current.importing) return;
    snapshotRef.current = next;
    setSnapshot(next);
    richieste.current.anteprima++;
    setLetturaAnteprima(false);
    setPreview(null);
    setEsito(null);
    setErrore("");
  }

  async function scegliFile(event) {
    if (richieste.current.importing) return;
    const file = event.target.files[0] || null;
    aggiornaSnapshot({ ...snapshotRef.current, file, mappatura: {} });
    setIntestazioni(null);
    const id = ++richieste.current.intestazioni;
    setLetturaFile(Boolean(file));
    if (!file) return;
    const attuale = () => richieste.current.mounted && id === richieste.current.intestazioni;
    const form = new FormData();
    form.append("uploaded_file", file);
    try {
      const response = await upload(baseUrl + "/intestazioni", form);
      const dati = await response.json();
      if (!attuale()) return;
      setIntestazioni(dati);
      aggiornaSnapshot({ ...snapshotRef.current, mappatura: { ...dati.mappaturaSuggerita } });
    } catch (error) {
      if (attuale()) setErrore(error.message);
    } finally {
      if (attuale()) setLetturaFile(false);
    }
  }

  const mancanti = intestazioni?.campi.filter(campo => campo.obbligatorio && !snapshot.mappatura[campo.chiave]) || [];
  const assegnate = Object.values(snapshot.mappatura).filter(Boolean);
  const duplicate = [...new Set(assegnate.filter((colonna, i) => assegnate.indexOf(colonna) !== i))];
  const scartate = intestazioni?.intestazioni.filter(colonna => !assegnate.includes(colonna)) || [];
  const mappaturaValida = Boolean(intestazioni) && !mancanti.length && !duplicate.length;
  const confermabile = preview && preview.snapshot === snapshot && !esito && preview.dati.riepilogo.articoliImportabili > 0;

  async function generaAnteprima() {
    if (!mappaturaValida || !snapshot.nuovoFinoAl || richieste.current.importing) return;
    const selezione = snapshotRef.current;
    const id = ++richieste.current.anteprima;
    const attuale = () => richieste.current.mounted && id === richieste.current.anteprima && selezione === snapshotRef.current;
    setPreview(null);
    setEsito(null);
    setErrore("");
    setLetturaAnteprima(true);
    try {
      const response = await upload(baseUrl + "/anteprima", formDaSnapshot(selezione));
      const dati = await response.json();
      if (attuale()) setPreview({ snapshot: selezione, dati });
    } catch (error) {
      if (attuale()) setErrore(error.message);
    } finally {
      if (attuale()) setLetturaAnteprima(false);
    }
  }

  async function importa() {
    if (!confermabile || preview.snapshot !== snapshotRef.current || richieste.current.importing) return;
    richieste.current.importing = true;
    setImportazione(true);
    setErrore("");
    try {
      const response = await upload(baseUrl + "/importa", formDaSnapshot(preview.snapshot));
      const dati = await response.json();
      if (richieste.current.mounted) setEsito(dati);
    } catch (error) {
      if (richieste.current.mounted) setErrore(error.message);
    } finally {
      richieste.current.importing = false;
      if (richieste.current.mounted) setImportazione(false);
    }
  }

  return (
    <div className="pa3">
      <h2>Rossignol group v2</h2>
      <p>CSV UTF-8 con EAN e dati CATALOG EXPORT. I dati gestionali vengono letti dalla cache Moda tramite EAN.</p>
      <p aria-live="polite">Passo {esito ? "4 di 4: Esito" : preview ? "3 di 4: Anteprima" : intestazioni ? "2 di 4: Mappatura" : "1 di 4: File e Nuovo fino al"}</p>
      <fieldset disabled={importazione} className="bn pa0 ma0">
        <legend className="f4 b mv3">1. File e Nuovo fino al</legend>
        <p>Il backend calcola le stagioni per ogni articolo: la stagione collezione deriva da stagione_lancio Moda,
          con fallback sulla stagione d’acquisto. La stagione consigliata deriva dalla stagione d’acquisto:
          se termina con 2, AUTUNNO INVERNO; se termina con 1, PRIMAVERA ESTATE.</p>
        <div className="mb3">
          <p>Nuovo fino al: {formatDataPerTasto(snapshot.nuovoFinoAl)}</p>
          {date.map(data => <button key={data} type="button"
            className={snapshot.nuovoFinoAl === data ? buttonClassesGreen : buttonClassesWhite}
            aria-pressed={snapshot.nuovoFinoAl === data}
            onClick={() => aggiornaSnapshot({ ...snapshotRef.current, nuovoFinoAl: data })}>
            {formatDataPerTasto(data)}
          </button>)}
        </div>
        <ColonneAttese dati={colonneAttese} errore={erroreColonne} />
        <label htmlFor="rossignol-v2-file">File CSV UTF-8</label>
        <input id="rossignol-v2-file" type="file" accept=".csv,text/csv" onChange={scegliFile} className="db mv3" />
        {snapshot.file && <p>File selezionato: {snapshot.file.name}</p>}
        {letturaFile && <div role="status" aria-label="Lettura intestazioni"><BlockSkeleton height={180} /></div>}
        {snapshot.file && !intestazioni && !letturaFile && <p>Seleziona nuovamente il file per riprovare la lettura delle intestazioni.</p>}
        {intestazioni && <section aria-label="Mappatura campi">
          <h3>2. Mappatura</h3>
          <p>Righe del file: {intestazioni.righe} — Delimitatore: {intestazioni.delimitatore}</p>
          <p>Ogni colonna può essere usata una sola volta. Le colonne non mappate verranno scartate.</p>
          {intestazioni.campi.map(campo => <div key={campo.chiave} className="pa2 mv2 ba bordoColAlt br3">
            <label htmlFor={`rossignol-v2-campo-${campo.chiave}`} className="db mb2">
              {campo.etichetta} <span className="dib ph2 br3 sfondoColChiaro">{campo.obbligatorio ? "Obbligatorio" : "Facoltativo"}</span>
            </label>
            <select id={`rossignol-v2-campo-${campo.chiave}`} className={inputClassesNoMeasure}
              value={snapshot.mappatura[campo.chiave] || ""}
              onChange={event => aggiornaSnapshot({ ...snapshotRef.current, mappatura: {
                ...snapshotRef.current.mappatura, [campo.chiave]: event.target.value || null,
              } })}>
              <option value="">— non mappato —</option>
              {intestazioni.intestazioni.map(colonna => <option key={colonna} value={colonna}>{colonna}</option>)}
            </select>
            <p style={{ overflowWrap: "anywhere" }}>Esempio: {intestazioni.esempio?.[0]?.[snapshot.mappatura[campo.chiave]] || "—"}</p>
          </div>)}
          <div aria-label="Colonne scartate">
            <p className="b">Colonne che verranno scartate ({scartate.length})</p>
            {scartate.length ? <ul>{scartate.map(colonna => <li key={colonna}>{colonna}</li>)}</ul> : <p>Nessuna</p>}
          </div>
          {!!mancanti.length && <p role="alert">Mappa i campi obbligatori: {mancanti.map(campo => campo.etichetta).join(", ")}.</p>}
          {!!duplicate.length && <p role="alert">La stessa intestazione è usata per più campi: {duplicate.join(", ")}. Scegli colonne diverse.</p>}
          <button type="button" className={buttonClassesWhite} onClick={generaAnteprima}
            disabled={!mappaturaValida || !snapshot.nuovoFinoAl || letturaAnteprima || importazione}>Genera anteprima</button>
        </section>}
      </fieldset>
      {intestazioni && <section aria-label="Anteprima importazione" className="mv3">
        <h3>3. Anteprima</h3>
        {letturaAnteprima && <div role="status" aria-label="Generazione anteprima"><BlockSkeleton height={240} /><BlockSkeleton height={320} className="mt3" /></div>}
        {!preview && !letturaAnteprima && <p>Genera l’anteprima per confermare. Ogni modifica a file, mappatura o Nuovo fino al richiede una nuova anteprima.</p>}
        {preview && <>
          <Riepilogo dati={preview.dati.riepilogo} />
          <p>Primi {preview.dati.anteprima.length} articoli importabili (massimo 5), nell’ordine del file.</p>
          {preview.dati.anteprima.slice(0, 5).map(dati => <ArticoloAnteprima key={`${dati.riga}-${dati.codVS}`} dati={dati} />)}
        </>}
        <button type="button" className={buttonClassesGreen} disabled={!confermabile || importazione || letturaAnteprima}
          onClick={importa}>Conferma importazione di {preview?.dati.riepilogo.articoliImportabili || 0} articoli</button>
        {importazione && <div role="status" aria-label="Importazione in corso"><BlockSkeleton height={180} className="mt3" /></div>}
      </section>}
      {errore && <p role="alert" className="pa3 ba bordoColAlt">{errore}</p>}
      {esito && <section aria-label="Esito importazione">
        <h3>4. Esito</h3>
        <Riepilogo dati={esito.riepilogo} />
        <Elenco titolo="Importati" valori={esito.importati} />
        <Elenco titolo="Codici già pubblicati" valori={esito.codiciGiaPubblicati} />
        <Elenco titolo="EAN non trovati nell’importazione" valori={esito.eanNonTrovati} />
        <h4>Messaggi per codice VS</h4>
        {Object.entries(esito.messaggiComplessivi || {}).map(([codice, messaggi]) => <Elenco key={codice} titolo={codice} valori={messaggi} />)}
      </section>}
    </div>
  );
}
